/**
 * /admin/agent-usage — inline-agent token consumption, cached-vs-uncached
 * share, and estimated cost, broken down per user and per app. Source is the
 * shared Analytics Engine dataset, filtered to agent_turn events. Cost is
 * estimated. The selected range is kept in the URL (?range=) so it survives a
 * refresh and is shareable.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../../api/client.js';
import type {
  AgentUsageAppRow,
  AgentUsageResponse,
  AgentUsageUserRow,
  UsageRange,
} from '../../api/types.js';
import { AdminTabs } from '../../components/AdminTabs.js';
import { IconBolt, IconInfo, IconRocket, IconUsers } from '../../components/Icons.js';
import {
  Banner,
  Card,
  CardHeader,
  EmptyState,
  PageHeader,
  PageSkeleton,
} from '../../components/ui.js';

interface State {
  loading: boolean;
  data?: AgentUsageResponse;
  error?: string;
}

const RANGE_OPTIONS: { value: UsageRange; label: string }[] = [
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: '90d', label: '90 days' },
];

const RANGE_VALUES = RANGE_OPTIONS.map((o) => o.value) as string[];

/** Read the range from the URL, falling back to 'week' for missing/invalid. */
function asRange(v: string | null): UsageRange {
  return v && RANGE_VALUES.includes(v) ? (v as UsageRange) : 'week';
}

const usd = (n: number) =>
  n.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });
const pct = (n: number) => `${(n * 100).toFixed(0)}%`;
const num = (n: number) => Math.round(n).toLocaleString();

export function AdminAgentUsage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const range = asRange(searchParams.get('range'));
  const [state, setState] = useState<State>({ loading: true });

  const setRange = (value: UsageRange) => {
    setSearchParams(
      (prev) => {
        prev.set('range', value);
        return prev;
      },
      { replace: true },
    );
  };

  const load = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true }));
    try {
      const data = await api.get<AgentUsageResponse>(`/api/admin/agent-usage?range=${range}`);
      setState({ loading: false, data });
    } catch (err) {
      setState({ loading: false, error: err instanceof ApiError ? err.message : String(err) });
    }
  }, [range]);

  useEffect(() => {
    void load();
  }, [load]);

  if (state.loading && !state.data) return <PageSkeleton rows={4} />;
  if (state.error) return <Banner tone="error">{state.error}</Banner>;
  const data = state.data;
  if (!data) return null;

  return (
    <div className="animate-fade-in">
      <PageHeader
        eyebrow="Admin"
        title="Agent usage"
        description="Per-user and per-app inline-agent token consumption and estimated cost. Costs are estimates."
      />

      <AdminTabs />

      <div className="flex items-center justify-between gap-4 mb-5 flex-wrap">
        <div
          role="tablist"
          aria-label="Time range"
          className="inline-flex items-center gap-1 p-1 rounded-lg bg-surface-2 border border-line"
        >
          {RANGE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              role="tab"
              aria-selected={range === opt.value}
              type="button"
              onClick={() => setRange(opt.value)}
              className={
                'px-3 py-1 rounded-md text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/50 ' +
                (range === opt.value
                  ? 'bg-surface text-ink shadow-card'
                  : 'text-muted hover:text-ink')
              }
            >
              {opt.label}
            </button>
          ))}
        </div>
        <p className="text-xs text-faint inline-flex items-center gap-1.5">
          <IconInfo size={12} />
          Estimated from Analytics Engine; updates roughly once a minute.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-5">
        <KpiCard
          label="Estimated cost"
          value={usd(data.totals.estCostUsd)}
          hint="in the selected range"
          icon={<IconBolt size={12} />}
        />
        <KpiCard
          label="Cached"
          value={pct(data.totals.cachedShare)}
          hint="of input-side tokens served from cache"
          icon={<IconBolt size={12} />}
        />
        <KpiCard
          label="Turns"
          value={num(data.totals.turns)}
          hint="agent turns"
          icon={<IconUsers size={12} />}
        />
      </div>

      <Card className="mb-5">
        <CardHeader
          title="By user"
          description={`${data.users.length} user${data.users.length === 1 ? '' : 's'} with agent activity · sorted by estimated cost`}
        />
        {data.users.length === 0 ? (
          <div className="px-5 pb-5">
            <EmptyState
              icon={<IconUsers size={18} />}
              title="No agent usage yet"
              description="Once people use the in-app agent, their token consumption shows up here."
            />
          </div>
        ) : (
          <UsageTable
            rows={data.users}
            firstHeader="User"
            rowKey={(u) => u.user_id}
            firstCell={(u: AgentUsageUserRow) =>
              u.email ? (
                <span className="font-medium text-ink">{u.email}</span>
              ) : u.user_id === 'unknown' ? (
                <span className="text-faint italic">Unknown</span>
              ) : (
                <span className="text-faint font-mono">{u.user_id}</span>
              )
            }
          />
        )}
      </Card>

      <Card>
        <CardHeader
          title="By app"
          description={`${data.apps.length} app${data.apps.length === 1 ? '' : 's'} with agent activity · sorted by estimated cost`}
        />
        {data.apps.length === 0 ? (
          <div className="px-5 pb-5">
            <EmptyState
              icon={<IconRocket size={18} />}
              title="No agent usage yet"
              description="Per-app agent token consumption shows up here once apps use the agent."
            />
          </div>
        ) : (
          <UsageTable
            rows={data.apps}
            firstHeader="App"
            rowKey={(a) => a.app_id}
            firstCell={(a: AgentUsageAppRow) =>
              a.slug ? (
                <Link
                  to={`/admin/apps/${a.slug}`}
                  className="font-medium text-ink hover:text-brand transition-colors"
                >
                  {a.name ?? a.slug}
                </Link>
              ) : (
                <span className="text-faint italic">deleted app · {a.app_id}</span>
              )
            }
          />
        )}
      </Card>
    </div>
  );
}

interface MetricRow {
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheCreationTokens: number;
  cachedShare: number;
  turns: number;
  estCostUsd: number;
}

/** The shared token table — same columns for the per-user and per-app views. */
function UsageTable<T extends MetricRow>({
  rows,
  firstHeader,
  rowKey,
  firstCell,
}: {
  rows: T[];
  firstHeader: string;
  rowKey: (row: T) => string;
  firstCell: (row: T) => React.ReactNode;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs uppercase tracking-wider text-muted font-medium">
            <th className="text-left font-medium px-5 py-2.5">{firstHeader}</th>
            <th className="text-right font-medium px-3 py-2.5 tabular">Input</th>
            <th className="text-right font-medium px-3 py-2.5 tabular">Output</th>
            <th className="text-right font-medium px-3 py-2.5 tabular">Cache read</th>
            <th className="text-right font-medium px-3 py-2.5 tabular">Cache write</th>
            <th className="text-right font-medium px-3 py-2.5 tabular">Cached %</th>
            <th className="text-right font-medium px-3 py-2.5 tabular">Turns</th>
            <th className="text-right font-medium px-5 py-2.5 tabular">Est. cost</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={rowKey(r)} className="border-t border-line">
              <td className="px-5 py-2.5 min-w-0">{firstCell(r)}</td>
              <td className="px-3 py-2.5 text-right tabular text-ink">{num(r.inputTokens)}</td>
              <td className="px-3 py-2.5 text-right tabular text-ink">{num(r.outputTokens)}</td>
              <td className="px-3 py-2.5 text-right tabular text-ink">{num(r.cacheReadTokens)}</td>
              <td className="px-3 py-2.5 text-right tabular text-ink">
                {num(r.cacheCreationTokens)}
              </td>
              <td className="px-3 py-2.5 text-right tabular text-ink">{pct(r.cachedShare)}</td>
              <td className="px-3 py-2.5 text-right tabular text-ink">{num(r.turns)}</td>
              <td className="px-5 py-2.5 text-right tabular font-medium text-ink">
                {usd(r.estCostUsd)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function KpiCard({
  label,
  value,
  hint,
  icon,
}: {
  label: string;
  value: string;
  hint: string;
  icon: React.ReactNode;
}) {
  return (
    <Card>
      <div className="p-5">
        <div className="text-xs uppercase tracking-wider text-muted font-medium inline-flex items-center gap-1.5">
          {icon} {label}
        </div>
        <div className="font-display text-4xl font-semibold tabular text-ink mt-2">{value}</div>
        <div className="text-xs text-muted mt-1">{hint}</div>
      </div>
    </Card>
  );
}
